// @flow
import {compose, either, isEmpty, isNil, not, path, propEq, propOr, reject} from 'ramda'
import typedLiftNodes from 'utils/GraphQL/typedLiftNodes'
import {makeValues, pickChildrenOfType}
  from 'components/proposals/Part/shared/fetchFieldsFromChange'
import type Item from '../Item'
import Value from '../ui/Value'
import {makeItems} from '../Item'
import type {Alert, Change, Circle} from './types'

const isBlank = either(isNil, isEmpty)

const hasSupportingCircle = compose(not, isNil, path(['supportingCircle']))

const getIsProtected = propOr(false, 'isProtected')

const liftPolicies = (obj) => typedLiftNodes(obj?.policies)
const liftRoles = (obj) => typedLiftNodes(obj?.roles)
const liftCirclePolicies = (obj) => typedLiftNodes(obj?.circlePolicies)
const liftCircleRoles = (obj) => typedLiftNodes(obj?.circleRoles)

class FetchFields {
  accountabilities: $ReadOnlyArray<Value>
  alerts: $ReadOnlyArray<Alert>
  body: Object
  circle: ?Circle
  destroyRole: boolean
  domains: $ReadOnlyArray<Value>
  roleTemplateId: ?string
  expandRole: boolean
  nextName: string
  nextPurpose: string
  role: Object
  selectedPoliciesToMoveDown: ?$ReadOnlyArray<Object>
  selectedPoliciesToMoveUp: ?$ReadOnlyArray<Object>
  selectedRolesToMoveDown: ?$ReadOnlyArray<Object>
  selectedRolesToMoveUp: ?$ReadOnlyArray<Object>
  isWysiwygEnabled: boolean
  isOnV4: boolean

  constructor(change: Change) {
    const {
      role,
      circle,
      organization,
      rolesToMoveUp,
      rolesToMoveDown,
      policiesToMoveUp,
      policiesToMoveDown,
      command = '{}',
    } = change
    this.body = JSON.parse(command)
    const {name: nextName, purpose: nextPurpose, roleTemplateId} = this.body

    this.role = role
    this.circle = circle
    this.nextName = nextName
    this.nextPurpose = nextPurpose
    this.roleTemplateId = roleTemplateId
    this.isOnV4 = organization?.isOnV4 || false
    this.isWysiwygEnabled = organization?.isWysiwygEnabled || false

    this.domains = makeValues(
      typedLiftNodes(role?.domains).map(({
        databaseId,
        isProtected,
        rawDescription,
        markdownDescription,
      }) => ({
        databaseId,
        isProtected,
        description: this.isWysiwygEnabled ? markdownDescription : rawDescription,
      })),
      pickChildrenOfType('Domain', this.body),
    )

    this.accountabilities = makeValues(
      typedLiftNodes(role?.accountabilities).map(({
        databaseId,
        isProtected,
        rawDescription,
        markdownDescription,
      }) => ({
        databaseId,
        isProtected,
        description: this.isWysiwygEnabled ? markdownDescription : rawDescription,
      })),
      pickChildrenOfType('Accountability', this.body),
    )

    this.selectedRolesToMoveDown = rolesToMoveDown
    this.selectedRolesToMoveUp = rolesToMoveUp
    this.selectedPoliciesToMoveDown = policiesToMoveDown
    this.selectedPoliciesToMoveUp = policiesToMoveUp
  }

  nameAndPurpose(): Value[] {
    if (isBlank(this.role)) {
      return [
        new Value('', this.nextName, {isNew: true}),
        new Value('', this.nextPurpose, {isNew: true}),
      ]
    }

    const prevName = this.role.name
    const prevPurpose = this.isWysiwygEnabled
      ? this.role.mdPurpose
      : this.role.rawPurpose

    return [
      new Value(prevName, this.nextName),
      new Value(prevPurpose, this.nextPurpose),
    ]
  }

  get policiesToMoveDown() {
    return makeItems(liftPolicies, this.circle, this.selectedPoliciesToMoveDown)
  }

  get policiesToMoveUp() {
    return makeItems(liftCirclePolicies, this.role, this.selectedPoliciesToMoveUp)
  }

  get rolesToMoveDown() {
    const databaseId = this.role ? this.role.databaseId : 'does-not-exist'
    const selectRoles = compose(reject(propEq('databaseId', databaseId)), liftRoles)
    return makeItems(selectRoles, this.circle, this.selectedRolesToMoveDown)
  }

  get rolesToMoveUp() {
    return makeItems(liftCircleRoles, this.role, this.selectedRolesToMoveUp)
  }

  call() {
    const [nameValue, purposeValue] = this.nameAndPurpose()

    return {
      accountabilities: this.accountabilities,
      // Destroy the role itself?  Toggled on or off only when a circle is not
      // involved.  It is always false when there is a circle involved, in order
      // to support the existing UI workflow.
      destroyRole: !!this.body.destroy,
      // Destroy the circle and _possibly_ destroy the role?
      destroyCircle: !!this.body.destroyCircle,
      // Destroy the circle and _keep_ the role?
      destroyCircleKeepRole: !!this.body.destroyCircleKeepRole,
      domains: this.domains,
      expandRole: !!this.body.expand,
      restructureCircle: !!this.body.restructure,
      isOnV4: this.isOnV4,
      isCircle: hasSupportingCircle(this.role),
      isProtected: getIsProtected(this.role),
      name: nameValue,
      roleTemplateId: this.roleTemplateId,
      parentCircleName: this.circle?.localizedName || '',
      policiesToMoveDown: this.policiesToMoveDown,
      policiesToMoveUp: this.policiesToMoveUp,
      purpose: purposeValue,
      rolesToMoveDown: this.rolesToMoveDown,
      rolesToMoveUp: this.rolesToMoveUp,
    }
  }
}

export default (change: Change): {
  accountabilities: $ReadOnlyArray<Value>,
  destroyCircle: any,
  destroyCircleKeepRole: any,
  destroyRole: any,
  domains: $ReadOnlyArray<Value>,
  expandRole: any,
  isCircle: any,
  isOnV4: boolean,
  isProtected: any,
  name: Value,
  parentCircleName: string,
  policiesToMoveDown: Array<Item>,
  policiesToMoveUp: Array<Item>,
  purpose: Value,
  restructureCircle: any,
  roleTemplateId: ?string,
  rolesToMoveDown: Array<Item>,
  rolesToMoveUp: Array<Item>,
} => (new FetchFields(change)).call()
